import React from 'react';
import { MOTOR_TYPES } from '../../../../constants';
import { getProductMotorSettings } from '../../../../utils';
import { useLanguage } from '../../../../i18n/LanguageContext';

/**
 * OrderMotorSection
 * Sipariş oluşturma formlarında motorlu ürünler için 3 firma ve Motor / Motor + Kumanda seçimi sağlar.
 * 8 ürün tipinde kullanılır: stor, zebra, ahsapJaluzi, metalJaluzi, dikeyPerde, dikeyPvc, dikeyStor, katlamaliPerde
 */
const OrderMotorSection = ({
  newOrder,
  onFieldChange,
  adminSettings,
  selectedProductType,
  isLoading
}) => {
  const { t } = useLanguage();
  const motorSettings = getProductMotorSettings(adminSettings, selectedProductType);
  const brands = motorSettings.brands || [];

  const selectedCompanyId = newOrder.motorCompany || '';
  const selectedOption = newOrder.motorType || ''; // 'Motor' veya 'Motor + Kumanda'

  // Firma seçimi değiştiğinde
  const handleCompanyChange = (e) => {
    const compId = e.target.value;
    if (!compId) {
      onFieldChange('motorCompany', '');
      onFieldChange('motorCompanyName', '');
      onFieldChange('motorType', '');
      onFieldChange('motorPriceCurrency', '');
      onFieldChange('motorPrice', 0);
      return;
    }

    const brand = brands.find(b => b.id === compId);
    const brandName = brand ? brand.name : compId;
    onFieldChange('motorCompany', compId);
    onFieldChange('motorCompanyName', brandName);

    // Eğer henüz seçenek seçilmediyse varsayılan olarak 'Motor' seçilsin
    const optionToUse = selectedOption || MOTOR_TYPES.MOTOR_1;
    onFieldChange('motorType', optionToUse);

    // Fiyatı hesapla
    calculateAndSetPrice(brand, optionToUse);
  };

  // Seçenek (Motor vs Motor + Kumanda) değiştiğinde
  const handleOptionChange = (e) => {
    const opt = e.target.value;
    onFieldChange('motorType', opt);

    if (!opt) {
      onFieldChange('motorPriceCurrency', '');
      onFieldChange('motorPrice', 0);
      return;
    }

    // Eğer firma seçili değilse ilk firmayı varsayılan yap
    let compId = selectedCompanyId;
    let brand = brands.find(b => b.id === compId);
    if (!brand && brands.length > 0) {
      brand = brands[0];
      compId = brand.id;
      onFieldChange('motorCompany', compId);
      onFieldChange('motorCompanyName', brand.name);
    }

    calculateAndSetPrice(brand, opt);
  };

  const calculateAndSetPrice = (brand, option) => {
    if (!brand) return;
    const mPrice = parseFloat(brand.motorPrice) || 0;
    const kPrice = parseFloat(brand.kumandaPrice) || 0;

    let totalCurrency = 0;
    if (option === MOTOR_TYPES.MOTOR_2_KUMANDA) {
      totalCurrency = mPrice + kPrice;
    } else {
      totalCurrency = mPrice;
    }

    const finalPrice = totalCurrency > 0 ? totalCurrency : (brand.motorPrice || '');
    onFieldChange('motorPriceCurrency', finalPrice);
    onFieldChange('motorPrice', finalPrice ? parseFloat(finalPrice) : 0);
  };

  const currentBrand = brands.find(b => b.id === selectedCompanyId);

  return (
    <div style={{
      gridColumn: '1 / -1',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '12px 14px',
      margin: '8px 0 12px 0'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="fas fa-cog" style={{ color: '#b04309' }}></i>
          <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
            {t('productList.orderMotor.title', 'Motor & Kumanda Seçimi')}
          </span>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '12px'
      }}>
        {/* Firma Seçimi */}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
            {t('productList.orderMotor.company', 'Motor Firması')}
          </label>
          <select
            value={selectedCompanyId}
            onChange={handleCompanyChange}
            disabled={isLoading}
            style={{ width: '100%' }}
          >
            <option value="">{t('productList.orderMotor.withoutMotor', 'Motorsuz')}</option>
            {brands.map(b => (
              <option key={b.id} value={b.id}>
                {b.name} ({t('productList.orderMotor.motor', 'Motor')}: {b.motorPrice || '0'} €{b.kumandaPrice ? ` | ${t('productList.orderMotor.remote', 'Kumanda')}: ${b.kumandaPrice} €` : ''})
              </option>
            ))}
          </select>
        </div>

        {/* Seçenek: Motor veya Motor + Kumanda */}
        {selectedCompanyId && (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
              {t('productList.orderMotor.package', 'Motor / Kumanda Paketi')}
            </label>
            <select
              value={selectedOption}
              onChange={handleOptionChange}
              disabled={isLoading}
              style={{ width: '100%' }}
            >
              <option value="">{t('productList.orderMotor.select', 'Seçiniz')}</option>
              {Object.entries(MOTOR_TYPES).map(([key, value]) => (
                <option key={key} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Ücret Alanı */}
        {selectedCompanyId && selectedOption && (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
              {t('productList.orderMotor.motorTotal', 'Motor Toplam (€)')}
            </label>
            <input
              type="number"
              step="0.01"
              value={newOrder.motorPriceCurrency || ''}
              onChange={(e) => {
                const val = e.target.value;
                onFieldChange('motorPriceCurrency', val);
                onFieldChange('motorPrice', val ? parseFloat(val) : 0);
              }}
              disabled={isLoading}
            />
          </div>
        )}
      </div>

      {currentBrand && (
        <div style={{
          marginTop: '8px',
          fontSize: '0.75rem',
          color: '#64748b',
          display: 'flex',
          gap: '16px'
        }}>
          <span>{t('productList.orderMotor.motor', 'Motor')}: <strong>{currentBrand.motorPrice || 0} €</strong></span>
          <span>{t('productList.orderMotor.remote', 'Kumanda')}: <strong>{currentBrand.kumandaPrice || 0} €</strong></span>
          {selectedOption === MOTOR_TYPES.MOTOR_2_KUMANDA && (
            <span style={{ color: '#047857', fontWeight: 600 }}>
              {t('productList.orderMotor.remoteIncluded', '✓ Kumanda ücreti dahil edildi')}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(OrderMotorSection);
